import React, { useState, useRef, useEffect, useCallback, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smile, Loader2 } from "lucide-react";

// Lazy load EmojiPicker for better performance
const EmojiPicker = lazy(() => import('emoji-picker-react'));

/**
 * MentionInput - A textarea with @mention support
 *
 * Props:
 * - value: string - Current input value
 * - onChange: (value: string) => void - Called when input changes
 * - onMentionsChange: (mentions: Array<{email, name}>) => void - Called when mentioned users change
 * - teamMembers: Array<{email, name, avatar_url?}> - List of users that can be mentioned
 * - placeholder: string - Placeholder text
 * - rows: number - Number of rows for textarea
 * - disabled: boolean - Whether input is disabled
 * - className: string - Additional CSS classes
 * - userEmail: string - Current user's email (to exclude from suggestions)
 */
const MentionInput = ({
  value,
  onChange,
  onMentionsChange,
  teamMembers = [],
  placeholder = "Type @ to mention someone...",
  rows = 3,
  disabled = false,
  className = "",
  userEmail = "",
  showEmojiButton = true,
}) => {
  const [showMentionPopup, setShowMentionPopup] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionCursorPos, setMentionCursorPos] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef(null);
  const popupRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Handle emoji selection
  const onEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    const cursorPos = textareaRef.current?.selectionStart || value.length;
    const newValue = value.slice(0, cursorPos) + emoji + value.slice(cursorPos);
    onChange(newValue);
    setShowEmojiPicker(false);

    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = cursorPos + emoji.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Extract mentioned users from text
  const extractMentions = useCallback((text) => {
    const mentionRegex = /@(\w+(?:\.\w+)*(?:@[\w.]+)?)/g;
    const mentions = [];
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      const mentionText = match[1].toLowerCase();
      // Find matching team member
      const member = teamMembers.find(m =>
        m.name?.toLowerCase().replace(/\s+/g, '').includes(mentionText.replace(/\s+/g, '')) ||
        m.email?.toLowerCase().includes(mentionText) ||
        m.name?.split(' ')[0]?.toLowerCase() === mentionText
      );
      if (member && !mentions.find(m => m.email === member.email)) {
        mentions.push({ email: member.email, name: member.name });
      }
    }

    return mentions;
  }, [teamMembers]);

  // Notify parent of mention changes
  useEffect(() => {
    const mentions = extractMentions(value);
    onMentionsChange?.(mentions);
  }, [value, extractMentions, onMentionsChange]);

  // Normalize team member data to handle different API response structures
  const normalizeTeamMember = useCallback((m) => ({
    email: m.email || m.user_email || m.memberEmail || '',
    name: m.name || m.display_name || m.displayName || m.firstName ? `${m.firstName || ''} ${m.lastName || ''}`.trim() : null,
    avatar_url: m.avatar_url || m.avatarUrl || m.avatar || null,
  }), []);

  // Handle @mention detection
  const handleMentionDetection = useCallback((text, cursorPosition) => {
    const textBeforeCursor = text.slice(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const searchTerm = mentionMatch[1].toLowerCase();
      // Filter team members - show all (except self) when searchTerm is empty
      const suggestions = teamMembers
        .map(normalizeTeamMember)
        .filter(m => {
          // Must have email
          if (!m.email) return false;
          // Exclude current user
          if (m.email === userEmail) return false;
          // If no search term, include all
          if (!searchTerm) return true;
          // Otherwise filter by name or email
          return (
            m.name?.toLowerCase().includes(searchTerm) ||
            m.email?.toLowerCase().includes(searchTerm)
          );
        }).slice(0, 6);

      setMentionSuggestions(suggestions);
      setShowMentionPopup(suggestions.length > 0);
      setMentionCursorPos(cursorPosition);
      setSelectedIndex(0);
    } else {
      setShowMentionPopup(false);
    }
  }, [teamMembers, userEmail, normalizeTeamMember]);

  // Handle input change
  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    onChange(newValue);
    handleMentionDetection(newValue, cursorPos);
  };

  // Insert mention
  const insertMention = (member) => {
    const textBeforeCursor = value.slice(0, mentionCursorPos);
    const textAfterCursor = value.slice(mentionCursorPos);
    const mentionStart = textBeforeCursor.lastIndexOf('@');

    const displayName = member.name?.split(' ')[0] || member.email.split('@')[0];
    const newText = textBeforeCursor.slice(0, mentionStart) +
      `@${displayName} ` +
      textAfterCursor;

    onChange(newText);
    setShowMentionPopup(false);

    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mentionStart + displayName.length + 2;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showMentionPopup) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, mentionSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && mentionSuggestions[selectedIndex]) {
      e.preventDefault();
      insertMention(mentionSuggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowMentionPopup(false);
    }
  };

  // Close popups on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) &&
          textareaRef.current && !textareaRef.current.contains(e.target)) {
        setShowMentionPopup(false);
      }
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target) &&
          !e.target.closest('.emoji-toggle-btn')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={`w-full px-3 py-2 pr-10 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none ${className}`}
      />

      {/* Emoji Button */}
      {showEmojiButton && (
        <button
          type="button"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          disabled={disabled}
          className={`emoji-toggle-btn absolute right-2 top-2 p-1.5 rounded-lg transition ${
            showEmojiPicker
              ? 'bg-slate-200 dark:bg-slate-700 text-yellow-500'
              : 'text-slate-400 hover:text-yellow-500 hover:bg-slate-100 dark:hover:bg-slate-700'
          } disabled:opacity-50`}
          title="Add emoji"
        >
          <Smile size={18} />
        </button>
      )}

      {/* Emoji Picker Popup with Backdrop */}
      <AnimatePresence>
        {showEmojiPicker && (
          <>
            {/* Backdrop to close on click outside */}
            <div
              className="fixed inset-0 z-[99]"
              onClick={() => setShowEmojiPicker(false)}
            />
            <motion.div
              ref={emojiPickerRef}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute top-full right-0 mt-1 z-[100] shadow-2xl rounded-xl overflow-hidden border dark:border-slate-700 border-slate-200"
            >
              <Suspense fallback={
                <div className="w-[320px] h-[400px] dark:bg-slate-900 bg-white flex items-center justify-center">
                  <Loader2 size={24} className="text-sky-500 animate-spin" />
                </div>
              }>
                <EmojiPicker
                  theme="auto"
                  onEmojiClick={onEmojiClick}
                  searchDisabled={false}
                  skinTonesDisabled
                  width={320}
                  height={400}
                />
              </Suspense>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Mention Suggestions Popup - positioned below textarea */}
      <AnimatePresence>
        {showMentionPopup && mentionSuggestions.length > 0 && (
          <motion.div
            ref={popupRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-1 w-72 dark:bg-slate-800 bg-white rounded-xl shadow-2xl border dark:border-slate-700 border-slate-200 py-1 z-[100] max-h-64 overflow-y-auto"
          >
            <p className="px-3 py-1.5 text-[10px] text-slate-500 uppercase tracking-wide font-semibold">
              Mention someone
            </p>
            {mentionSuggestions.map((member, idx) => (
              <button
                key={member.email}
                type="button"
                onClick={() => insertMention(member)}
                className={`w-full px-3 py-2 text-left flex items-center gap-3 transition ${
                  idx === selectedIndex
                    ? 'bg-sky-500/20 dark:bg-sky-500/20'
                    : 'hover:bg-slate-100 dark:hover:bg-slate-700/50'
                }`}
              >
                {/* Avatar */}
                {member.avatar_url || member.avatarUrl ? (
                  <img
                    src={member.avatar_url || member.avatarUrl}
                    alt={member.name}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold">
                    {member.name?.[0]?.toUpperCase() || member.email[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                    {member.name || member.email.split('@')[0]}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {member.email}
                  </p>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Helper function to parse and render text with @mentions highlighted
 * Use this to display notes with formatted mentions
 */
export const renderTextWithMentions = (text, teamMembers = []) => {
  if (!text) return text;

  const parts = [];
  let lastIndex = 0;
  const mentionRegex = /@(\w+(?:\.\w+)*(?:@[\w.]+)?)/g;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Find the mentioned user
    const mentionText = match[1].toLowerCase();
    const mentionedUser = teamMembers.find(m =>
      m.email?.toLowerCase().includes(mentionText) ||
      m.name?.toLowerCase().replace(/\s+/g, '').includes(mentionText.replace(/\s+/g, '')) ||
      m.name?.split(' ')[0]?.toLowerCase() === mentionText
    );

    // Add the mention as a styled span
    parts.push(
      <span
        key={match.index}
        className="bg-sky-500/20 text-sky-600 dark:text-sky-400 px-1 rounded font-medium"
        title={mentionedUser?.email || match[1]}
      >
        @{mentionedUser?.name?.split(' ')[0] || match[1]}
      </span>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

export default MentionInput;
